import React from 'react';
import './MapLoadingOverlay.css';

function toChecklistItems({
  mapReady,
  groceryReady,
  gymsReady,
  safetyReady,
  thingsToDoReady,
  rentalsReady,
  rentalsProgress,
  rentalsBlocking,
}) {
  const rentalsLabel =
    rentalsProgress && rentalsProgress.total > 0
      ? `Rentals (${rentalsProgress.rendered}/${rentalsProgress.total})`
      : 'Rentals';

  return [
    { key: 'map', label: 'Map tiles', ready: !!mapReady },
    { key: 'grocery', label: 'Grocery stores', ready: !!groceryReady },
    { key: 'gyms', label: 'Gyms', ready: !!gymsReady },
    { key: 'safety', label: 'Safety overlay', ready: !!safetyReady },
    { key: 'things', label: 'Things to do', ready: thingsToDoReady !== false },
    { key: 'rentals', label: rentalsLabel, ready: rentalsBlocking ? !!rentalsReady : true },
  ];
}

/**
 * Checklist + progress overlay shown over the map while layers load.
 */
export default function MapLoadingOverlay({
  mapReady,
  groceryReady,
  gymsReady,
  safetyReady,
  thingsToDoReady,
  rentalsReady,
  rentalsProgress = null,
  rentalsBlocking = true,
  variant = 'overlay',
}) {
  const items = toChecklistItems({
    mapReady,
    groceryReady,
    gymsReady,
    safetyReady,
    thingsToDoReady,
    rentalsReady,
    rentalsProgress,
    rentalsBlocking,
  });

  const completeCount = items.filter((item) => item.ready).length;
  const totalCount = items.length;
  const rentalsTotal = rentalsProgress?.total ?? 0;
  const rentalsSourceTotal = rentalsProgress?.sourceTotal ?? rentalsTotal;
  const rentalsVisibleTotal = rentalsProgress?.visibleTotal ?? rentalsTotal;
  const rentalsRendered = rentalsProgress?.rendered ?? 0;
  const rentalsIndexed = rentalsProgress?.indexed ?? rentalsRendered;
  const rentalsPhase = rentalsProgress?.phase ?? 'rendering';
  const rentalsRatio = (() => {
    if (rentalsReady) return 1;
    if (rentalsTotal <= 0) return 0;

    const indexingRatio = Math.min(rentalsIndexed / rentalsTotal, 1);
    const renderingRatio = Math.min(rentalsRendered / rentalsTotal, 1);

    if (rentalsPhase === 'indexing') {
      // Indexing is real work before draw; expose it in progress instead of staying stuck at 0%.
      return indexingRatio * 0.45;
    }

    // After indexing, blend both phases so users see true end-to-end rental pipeline progress.
    return Math.min(0.45 + indexingRatio * 0.1 + renderingRatio * 0.45, 1);
  })();

  const weightedProgress = {
    map: mapReady ? 1 : 0,
    grocery: groceryReady ? 1 : 0,
    gyms: gymsReady ? 1 : 0,
    safety: safetyReady ? 1 : 0,
    things: thingsToDoReady !== false ? 1 : 0,
    rentals: rentalsRatio,
  };

  const weights = {
    map: 0.23,
    grocery: 0.15,
    gyms: 0.15,
    safety: 0.2,
    things: 0.05,
    rentals: 0.22,
  };

  const weightedPercent = Math.round(
    Object.entries(weights).reduce((acc, [key, weight]) => acc + (weightedProgress[key] ?? 0) * weight, 0) * 100
  );
  const allReady = completeCount === totalCount;
  const showBackgroundRentalsProgress =
    !rentalsBlocking && rentalsTotal > 0 && !rentalsReady && rentalsPhase !== 'idle';
  const percent = allReady && showBackgroundRentalsProgress ? 100 : Math.min(99, weightedPercent);
  const nextPending = items.find((item) => !item.ready);
  const activeLabel = nextPending ? `Working on ${nextPending.label.toLowerCase()}…` : null;
  const readinessSummary = `${completeCount}/${totalCount} map layers ready`;
  const rentalsDetail =
    rentalsTotal > 0
      ? `${Math.round((Math.min(rentalsRendered, rentalsTotal) / rentalsTotal) * 100)}% rentals rendered (${Math.min(rentalsRendered, rentalsTotal)}/${rentalsTotal})`
      : null;
  const rentalsCoverageDetail =
    rentalsSourceTotal > rentalsVisibleTotal && rentalsVisibleTotal > 0
      ? `Showing ${rentalsVisibleTotal.toLocaleString()} rentals near this map view (from ${rentalsSourceTotal.toLocaleString()} total)`
      : null;
  const rentalsPhaseDetail =
    showBackgroundRentalsProgress && rentalsTotal > 0
      ? rentalsPhase === 'indexing'
        ? 'Stage: Building rental lookup index'
        : rentalsPhase === 'viewport'
          ? 'Stage: Measuring visible map area'
          : 'Stage: Drawing rental markers'
      : null;
  const rentalsIndexingDetail =
    rentalsTotal > 0 && rentalsPhase === 'indexing'
      ? `Preparing nearby rentals index (${Math.min(rentalsIndexed, rentalsTotal)}/${rentalsTotal})`
      : null;
  const rentalsViewportIndexingDetail =
    rentalsTotal > 0 && rentalsPhase === 'viewport' && rentalsIndexed > 0
      ? `Pre-indexed ${Math.min(rentalsIndexed, rentalsTotal).toLocaleString()} rentals while sizing viewport`
      : null;

  const startedAt = Number(rentalsProgress?.startedAt) || 0;
  const updatedAt = Number(rentalsProgress?.updatedAt) || 0;
  const elapsedMs = startedAt > 0 && updatedAt >= startedAt ? updatedAt - startedAt : 0;
  const rentalsRatePerSecond = elapsedMs > 250 && rentalsRendered > 0 ? (rentalsRendered / elapsedMs) * 1000 : 0;
  const indexingRatePerSecond = elapsedMs > 250 && rentalsIndexed > 0 ? (rentalsIndexed / elapsedMs) * 1000 : 0;
  const remainingListings = Math.max(rentalsTotal - rentalsRendered, 0);
  const etaSeconds = rentalsRatePerSecond > 0 ? Math.ceil(remainingListings / rentalsRatePerSecond) : null;
  const indexingEtaSeconds = indexingRatePerSecond > 0 ? Math.ceil(Math.max(rentalsTotal - rentalsIndexed, 0) / indexingRatePerSecond) : null;
  const rentalsVelocityDetail =
    rentalsRatePerSecond > 0 && !rentalsReady
      ? `${Math.round(rentalsRatePerSecond)} listings/sec${etaSeconds != null ? ` • ~${etaSeconds}s remaining` : ''}`
      : null;
  const rentalsIndexingVelocityDetail =
    indexingRatePerSecond > 0 && rentalsPhase === 'indexing' && !rentalsReady
      ? `${Math.round(indexingRatePerSecond)} listings/sec indexing${indexingEtaSeconds != null ? ` • ~${indexingEtaSeconds}s to draw phase` : ''}`
      : null;

  const mapInteractiveHint =
    mapReady && showBackgroundRentalsProgress
      ? rentalsPhase === 'indexing'
        ? 'Map is ready — preparing rentals index before draw.'
        : 'Map is ready — rentals continue drawing in the background.'
      : null;

  const indexingPercent =
    rentalsTotal > 0 ? Math.round((Math.min(rentalsIndexed, rentalsTotal) / rentalsTotal) * 100) : 0;
  const titleText = (() => {
    if (rentalsPhase === 'viewport' && rentalsTotal > 0) {
      return `Loading map (${percent}%) — sizing visible rentals`;
    }
    if (allReady && showBackgroundRentalsProgress) {
      const readyPercent = Math.round((Math.min(rentalsRendered, rentalsTotal) / rentalsTotal) * 100);
      return `Map ready — finishing rentals (${readyPercent}% drawn)`;
    }
    if (rentalsPhase === 'indexing' && rentalsTotal > 0) {
      return `Loading map (${percent}%) — indexing rentals ${indexingPercent}%`;
    }
    return `Loading map (${percent}%)`;
  })();

  if (allReady && !showBackgroundRentalsProgress) return null;

  return (
    <div className={`map-loading-overlay map-loading-overlay--${variant}`} aria-live="polite" role="status">
      <p className="map-loading-overlay__title">{titleText}</p>
      <div
        className="map-loading-overlay__progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label="Map loading progress"
        aria-valuetext={titleText}
      >
        <div className="map-loading-overlay__progress-fill" style={{ width: `${percent}%` }} />
      </div>
      <p className="map-loading-overlay__hint">{readinessSummary}</p>
      {activeLabel && <p className="map-loading-overlay__hint">{activeLabel}</p>}
      {mapInteractiveHint && <p className="map-loading-overlay__hint">{mapInteractiveHint}</p>}
      {rentalsPhaseDetail && <p className="map-loading-overlay__hint">{rentalsPhaseDetail}</p>}
      {rentalsIndexingDetail && showBackgroundRentalsProgress && <p className="map-loading-overlay__hint">{rentalsIndexingDetail}</p>}
      {rentalsViewportIndexingDetail && showBackgroundRentalsProgress && <p className="map-loading-overlay__hint">{rentalsViewportIndexingDetail}</p>}
      {rentalsIndexingVelocityDetail && showBackgroundRentalsProgress && <p className="map-loading-overlay__hint">{rentalsIndexingVelocityDetail}</p>}
      {rentalsCoverageDetail && showBackgroundRentalsProgress && <p className="map-loading-overlay__hint">{rentalsCoverageDetail}</p>}
      {rentalsDetail && showBackgroundRentalsProgress && <p className="map-loading-overlay__hint">{rentalsDetail}</p>}
      {rentalsVelocityDetail && rentalsPhase !== 'indexing' && showBackgroundRentalsProgress && <p className="map-loading-overlay__hint">{rentalsVelocityDetail}</p>}
      <ul className="map-loading-overlay__checklist">
        {items.map((item) => (
          <ChecklistItem key={item.key} label={item.label} ready={item.ready} />
        ))}
      </ul>
    </div>
  );
}

function ChecklistItem({ label, ready }) {
  return (
    <li className={`map-loading-overlay__item ${ready ? 'map-loading-overlay__item--done' : ''}`}>
      <span className="map-loading-overlay__icon" aria-hidden="true">
        {ready ? '✓' : '○'}
      </span>
      <span className="map-loading-overlay__label">{label}</span>
    </li>
  );
}
